import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { AccessProfileService } from "../../../services/Access/AccessProfile/AccessProfileService";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { useLocation } from "react-router-dom";
import { AccessProfileModel } from "../../../models/Access/AccessProfile/AccessProfileModel";
import { ToastSettings } from "../../../utils/ToastSettings";
import { AccessPageModel } from "../../../models/Access/AccessPage/AccessPageModel";
import { AccessPageService } from "../../../services/Access/AccessPage/AccessPageService";

interface ContextProps {
  menu: any[];
  partnerMenu: any[];
  accessPages: AccessPageModel[];
  setMenu: Dispatch<SetStateAction<any[]>>;
  accessProfilesOptions: any[];
  accessProfiles: AccessProfileModel[];
  setAccessProfiles: Dispatch<SetStateAction<AccessProfileModel[]>>;
  accessProfile: AccessProfileModel | null;
  setAccessProfile: Dispatch<SetStateAction<AccessProfileModel | null>>;
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  error: string;
  isLoadingModalAccess: boolean;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleListPages: (params: any) => void;
  handleMenuList: (accessProfileId?: number) => void;
  handlePartnerMenuList: (partnerCode: string) => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handlePermission: (data: any) => Promise<boolean>;
  handlePartnerPermission: (data: any) => Promise<boolean>;
}

export const AccessProfileContext = createContext<ContextProps>(
  {} as ContextProps
);

interface AccessProfileProviderProps {
  children: ReactNode;
}

export const AccessProfileProvider = ({
  children,
}: AccessProfileProviderProps) => {
  const [menu, setMenu] = useState<any[]>([]);
  const [partnerMenu, setPartnerMenu] = useState<any[]>([]);
  const [accessProfile, setAccessProfile] = useState<AccessProfileModel | null>(
    null
  );
  const [accessProfiles, setAccessProfiles] = useState<AccessProfileModel[]>(
    []
  );
  const [accessPages, setAccessPages] = useState<AccessPageModel[]>([]);
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingModalAccess, setIsLoadingModalAccess] =
    useState<boolean>(true);
  const [accessProfilesOptions, setAccessProfilesOptions] = useState<
    any | null
  >(null);
  const [error, setError] = useState<string>("");

  const service = new AccessProfileService();
  const servicePages = new AccessPageService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (
      !params ||
      (!!params?.withPaginate &&
        !location.pathname.includes("/access/access-profiles/list"))
    ) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setAccessProfiles(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os níveis de acesso.");
    }
  }, [params, location.pathname]);

  const handleListPages = useCallback(
    async (params: any) => {
      try {
        setIsLoadingModalAccess(true);
        const [_Response, _Error] = await servicePages.list(params);
        setIsLoadingModalAccess(false);

        if (!!_Error) {
          return setError(_Response?.message || _Error);
        }

        setAccessPages(_Response?.items);
        setPages(_Response?.pagination);
        return setError("");
      } catch (e) {
        setIsLoadingModalAccess(false);
        setError("Não foi possível carregar as páginas de acesso.");
      }
    },
    [params, location.pathname]
  );

  const handleFetch = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar o nível de acesso");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message ||
          "Houve um erro ao deletar o nível de acesso"
      );
      return false;
    }
  };

  const handleMenuList = async (accessProfileId?: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.menu(accessProfileId);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setMenu(_Response?.data?.items);
      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(e.response?.data?.message || "Houve um erro ao listar o menu");
      return false;
    }
  };

  const handlePermission = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.permission(
        data
      );
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar permissão");
      return false;
    }
  };

  const handlePartnerMenuList = async (partnerCode: string) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.partnerMenu(partnerCode);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setPartnerMenu(_Response?.data?.items);
      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(e.response?.data?.message || "Houve um erro ao listar o menu");
      return false;
    }
  };

  const handlePartnerPermission = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] =
        await service.partnerPermission(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar permissão");
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  useEffect(() => {
    setAccessProfilesOptions(
      accessProfiles?.map((item: AccessProfileModel) => ({
        label: item.name,
        value: item.id,
      }))
    );
  }, [accessProfiles]);

  return (
    <AccessProfileContext.Provider
      value={{
        menu,
        partnerMenu,
        setMenu,
        accessProfilesOptions,
        accessProfiles,
        accessPages,
        setAccessProfiles,
        accessProfile,
        isLoadingModalAccess,
        setAccessProfile,
        params,
        setParams,
        pages,
        setPages,
        isLoading,
        error,
        setError,
        handleList,
        handleFetch,
        handleDelete,
        handleMenuList,
        handlePartnerMenuList,
        handleListPages,
        handlePermission,
        handlePartnerPermission,
      }}
    >
      {children}
    </AccessProfileContext.Provider>
  );
};

export const useAccessProfile = () => useContext(AccessProfileContext);
